<script setup lang="ts">

import {computed, inject, onMounted, ref, watch} from "vue";
import {type ApontamentoEntidadeVm, dataAsApontamentoEntidadeVm} from "@/domain/type/apontamentoEntidade.type";

import {useToastHelper} from "@/helpers/toast.helper";
import ApontamentoCadastro from "@/components/apontamentos/ApontamentoCadastro.vue";
import {useDialog} from "primevue/usedialog";
import {useConfirm} from "primevue/useconfirm";
import type {IApontamentoEntidadeEvent} from "@/domain/events/apontamentoEntidade.event";
import type ApontamentoEntidadeService from "@/domain/services/apontamentoEntidade.service";
import {useModalStore} from "@/stores/modal.store";
import {EModalId, type ModalApontamentoCadastro, type ModalApontamentoLista} from "@/domain/type/modal.type";
import {useAuthStore} from "@/stores/auth.store";

const {showRequestError} = useToastHelper();
const confirm = useConfirm();

const dialog = useDialog();
const modalStore = useModalStore();
const authStore= useAuthStore();

interface Props {
  propEntidadeService?: any,
  propCodigoEntidade?: string,
  propApontamentoEvent?: IApontamentoEntidadeEvent
}

//eventos

let apontamentoEvent: IApontamentoEntidadeEvent | null = null;

//props
const props = defineProps<Props>();


const dialogRef: any = inject('dialogRef');
const isStateComponentError = ref(false);


//entidadeService pode ser qualquer entidade que trablhe com os apontamentos,
// exemplo: ChamadoService, ContratoService, EmepresaService, AssuntoService
// as entidades que trabalham com apontamentos possuem os mesmos metodos para lidar com as operacoes:
// listarApontamento, gravarApontamento, removerApontamento
//
const entidadeService = ref<null | ApontamentoEntidadeService>();
const codigoEntidade = ref<string>("");
const cgpe = ref(authStore.getUserCgpe());


const opcoesFiltroApontamento = ref([{descricao: "Todos", value: 0}, {
  descricao: "Sistema",
  value: 1
}, {descricao: "Usuário", value: 2}]);

const filtroSelecionado = ref({descricao: "Usuário", value: 2});


const isLoading = ref(false);
const arrListaApontamentoLoading = ref<boolean[]>([]);
const apontamentoSelecionado = ref<ApontamentoEntidadeVm>();

const arrApontamentos = ref<ApontamentoEntidadeVm[]>([]);


const arrApontamentosFiltrados = computed(() => {

  if (filtroSelecionado.value.value == 1) {
    //Sistema
    return arrApontamentos.value.filter(a => a.tipo == 1);

  } else if (filtroSelecionado.value.value == 2) {

    //Usuario
    if (arrApontamentos.value != null) {
      return arrApontamentos.value.filter(a => a.tipo == 2);
    }

  } else {
    //Todos
    return arrApontamentos.value;
  }

  return null;

})


const listarApontamento = () => {


  isLoading.value = true;


  //Como entidadeService pode ser qualquer servico, aqui estamos deixando explicito que entidadeService é um ApontamentoEntidadeService,
  //pois o que nos interessa são apenas os métodos para lidar com apontamentos

  (entidadeService.value as ApontamentoEntidadeService)
      .listarApontamento({codigoEntidade: codigoEntidade.value,cgpe:cgpe.value})
      .then((ret) => {
        arrApontamentos.value = ret.data;
      })
      .catch((error) => {
        showRequestError(error);
      })
      .finally(() => {
        isLoading.value = false;
      })


}


const onRemoverApontamento = (event: Event, apontamento: ApontamentoEntidadeVm) => {

  //Define o registro selecionado
  apontamentoSelecionado.value = apontamento;


  confirm.require({
    target: event.currentTarget as HTMLElement,
    message: 'Deseja remover o apontamento selecionado?',
    icon: 'pi pi-question-circle',
    acceptProps: {
      label: "Sim",
    },
    rejectProps: {
      label: "Não",
      severity: 'secondary'
    },
    accept: () => {

      arrListaApontamentoLoading.value[apontamento.codigo] = true;

      entidadeService.value
          ?.removerApontamento({codigoApontamento: apontamento.codigo.toString(), codigoEntidade: codigoEntidade.value,cgpe:cgpe.value})
          .then(() => {

            //Remove o apontamento do array
            arrApontamentos.value = arrApontamentos.value.filter(a => a.codigo != apontamento.codigo);

            //Emite o evento
            apontamentoEvent?.emitApontamentoAtualizado(codigoEntidade.value);

          })
          .catch(error => {
            showRequestError(error);
          })
          .finally(() => {
            arrListaApontamentoLoading.value[apontamento.codigo] = false;
          });


    }

  });


}


const onAdicionarApontamento = () => {


  const modal = modalStore.getModal(EModalId.modalApontamentoCadastro);

  const params: ModalApontamentoCadastro = {
    entidadeService: entidadeService.value,
    codigoEntidade: codigoEntidade.value,
    apontamentoEvent: apontamentoEvent ?? undefined,
    cgpe:cgpe.value
  }

  //Adiciona os parametros na store
  modal?.setParam(params);

  dialog.open(ApontamentoCadastro, {
    props: {

      header: 'Adicionar apontamento',
      style: {
        width: '50vw',
      },
      breakpoints: {
        '1199px': '75vw',
        '575px': '90vw'
      },
      modal: true,
      closeOnEscape: false

    }

  });

}

//Fecha o modal
const onFechar = () => {
  //Fechar o modal
  //emit("fechar");

  //Se o componente foi aberto atraves de um Dynamic Dialog fecha-o
  if (dialogRef && dialogRef.value) {
    dialogRef.value.close();
  }
}


//Carregamento do componente
onMounted(() => {

  //Verifica se o componente foi inicializado de forma correta


  try {

    //Verifica se possui informacoes atraves das PROPS (componente adicionado em um template) ou de um MODAL
    const modalParams = modalStore.getModal(EModalId.modalApontamentoLista)?.getParam() as ModalApontamentoLista;

    if (modalParams) {

      //Iniciou de um modal

      //Verifica se foi informado entidadeService
      if (modalParams.entidadeService) {
        entidadeService.value = modalParams.entidadeService;
      }

      //Verifica se foi informado o codigoEntidade
      if (modalParams.codigoEntidade) {
        codigoEntidade.value = modalParams.codigoEntidade.toString();
      }

      //Verifica se foi informado o apontamentoEvent
      if (modalParams.apontamentoEvent) {
        apontamentoEvent = modalParams.apontamentoEvent;
      }

      //Verifica se foi informado o CGPE
      if(modalParams.cgpe){
        cgpe.value = modalParams.cgpe;
      }


    } else {

      //Iniciou de um template
      if (props.propEntidadeService) {
        entidadeService.value = props.propEntidadeService;
      }

      if (props.propCodigoEntidade) {
        codigoEntidade.value = props.propCodigoEntidade;
      }

      if (props.propApontamentoEvent) {
        apontamentoEvent = props.propApontamentoEvent;
      }

      if(props.propCgpe) {
        cgpe.value = props.propCgpe;
      }

    }



    //Define o status erro do componente
    isStateComponentError.value = !(entidadeService.value != null && codigoEntidade.value != "" && cgpe.value != "");

  } catch {
    isStateComponentError.value = true;
  }


  //Aciona o metodo de listagem caso nao possua erro durante a inicializacao e as informacoes forem definidas corretamente
  if (!isStateComponentError.value) {
    listarApontamento();
  }


  //Adiciona o ouvinte de evento
  if (apontamentoEvent != null) {
    watch(apontamentoEvent.listenerApontamentoAtualizado, () => {
      listarApontamento();
    });
  }

})
</script>

<template>


  <div v-if="!isStateComponentError" @keydown.esc="onFechar">
    <DataTable v-if="isLoading" :value="new Array(4)">
      <!-- col Usuario -->
      <Column header="Usuário">
        <template #body>
          <Skeleton></Skeleton>
        </template>
      </Column>

      <!-- col Data -->
      <Column header="Data">
        <template #body>
          <Skeleton></Skeleton>
        </template>
      </Column>

      <!-- col Descricao -->
      <Column header="Descrição">
        <template #body>
          <Skeleton></Skeleton>
        </template>
      </Column>

      <!-- col botao remocao -->
      <Column header="">
        <template #body>
          <Skeleton></Skeleton>
        </template>
      </Column>

    </DataTable>

    <DataTable v-if="!isLoading" :value="arrApontamentosFiltrados" v-model:selection="apontamentoSelecionado"
               selection-mode="single" striped-rows sort-field="dataHora" :sort-order="-1">

      <template #header>
        <div class="flex flex-wrap align-items-center justify-end gap-2 ">
          <Button @click="onAdicionarApontamento" icon="pi pi-plus" severity="success" label="Adicionar Apontamento"
                  raised/>
        </div>

        <div class="flex flex-wrap gap-2 align-items-center justify-content-start ">
          <div>
            Tipo de apontamento:
          </div>
          <div>
            <SelectButton v-model="filtroSelecionado" :options="opcoesFiltroApontamento" optionLabel="descricao"
                          dataKey="value" aria-labelledby="custom" :allow-empty="false">
            </SelectButton>
          </div>
        </div>
      </template>

      <!-- col Usuario -->
      <Column field="usuario" header="Usuário"></Column>

      <!-- col Data -->
      <Column field="dataHora" header="Data" dataType="date" sortable="" style="min-width: 11rem">
        <template #body="{data}">
          {{ (dataAsApontamentoEntidadeVm(data).dataHoraFormatada) }}
        </template>
      </Column>

      <!-- col Descricao -->
      <Column field="descricao" header="Descrição">
        <template #body="{data}">
          <p style="white-space: break-spaces;">{{ dataAsApontamentoEntidadeVm(data).descricao }}</p>
        </template>
      </Column>

      <!-- col botao remocao -->
      <Column header="">
        <template #body="{data}">

          <Button @click="onRemoverApontamento($event,data)"
                  :loading="arrListaApontamentoLoading[dataAsApontamentoEntidadeVm(data).codigo]"
                  v-if="data.tipo != '1'" icon="pi pi-times" size="small" severity="danger" text raised></Button>
        </template>
      </Column>

    </DataTable>
  </div>


  <div v-if="isStateComponentError" class="component-state-error ">
    <span class="p-error text-2xl">Component Error</span>
  </div>

</template>

<style scoped>

</style>